<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.showPackage") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #ef3405">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>

    <div class="col-12 package_return_actions" v-if="hasReturnActions">
      <div class="package_return_actions__header">
        <h5>{{ $t("RETURN_REQUESTS.confirmReturnRequest") }}</h5>
      </div>
      <div class="package_return_actions__buttons">
        <button
          v-if="isReturningStatus"
          type="button"
          class="package_action_btn package_action_btn--scan"
          @click="openScanDialog"
        >
          <i class="fal fa-qrcode"></i>
          <span>{{ $t("RETURN_REQUESTS.scanQr") }}</span>
        </button>

        <button
          v-if="isReturnedStatus"
          type="button"
          class="package_action_btn package_action_btn--pending"
          @click="openRedeliveryConfirm"
        >
          <i class="fal fa-redo"></i>
          <span>{{ $t("RETURN_REQUESTS.moveToPending") }}</span>
        </button>
      </div>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <form class="single_step_form_content_wrapper">
      <div class="row">
        <div class="form_title_wrapper">
          <h4>{{ $t("PLACEHOLDERS.package_data") }}</h4>
        </div>

        <div class="preview-container text-center my-3 col-12" v-if="data.qr">
          <img
            ref="qrImage"
            :src="data.qr"
            :alt="$t('PLACEHOLDERS.qr_code')"
            style="width: 200px; height: 200px"
          />
          <div class="mt-3">
            <v-btn color="primary" class="mx-2" @click="downloadQR">
              <i class="fal fa-download mr-2"></i>
              {{ $t("BUTTONS.download_qr") }}
            </v-btn>
            <v-btn color="secondary" class="mx-2" @click="printQR">
              <i class="fal fa-print mr-2"></i>
              {{ $t("BUTTONS.print_qr") }}
            </v-btn>
          </div>
        </div>

        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.package_number')"
          :value="data.package_number"
          disabled
        />
        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.bundle_number')"
          :value="data.bundle_number || '-'"
          disabled
        />
        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.tracking_token')"
          :value="data.tracking_token"
          disabled
        />
        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.address')"
          :value="data.address"
          disabled
        />
        <base-input
          col="3"
          type="text"
          :placeholder="$t('PLACEHOLDERS.weight')"
          :value="data.weight ? `${data.weight} ${$t('PLACEHOLDERS.kg')}` : ''"
          disabled
        />
        <base-input
          col="3"
          type="text"
          :placeholder="$t('PLACEHOLDERS.width')"
          :value="data.width ? `${data.width} ${$t('PLACEHOLDERS.cm')}` : ''"
          disabled
        />
        <base-input
          col="3"
          type="text"
          :placeholder="$t('PLACEHOLDERS.height')"
          :value="data.height ? `${data.height} ${$t('PLACEHOLDERS.cm')}` : ''"
          disabled
        />
        <base-input
          col="3"
          type="text"
          :placeholder="$t('PLACEHOLDERS.length')"
          :value="data.length ? `${data.length} ${$t('PLACEHOLDERS.cm')}` : ''"
          disabled
        />
        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.delivery_options')"
          :value="data.delivery_options_text"
          disabled
        />
        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.status')"
          :value="data.status_text"
          disabled
        />
        <base-input
          col="12"
          type="textarea"
          :placeholder="$t('PLACEHOLDERS.notes')"
          :value="data.notes"
          disabled
        />

        <hr class="w-75 m-auto mt-3 mb-4" />

        <div class="form_title_wrapper">
          <h4>{{ $t("PLACEHOLDERS.recipient_data") }}</h4>
        </div>
        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.name')"
          :value="data.user_name"
          disabled
        />
        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.phone')"
          :value="data.mobile"
          disabled
        />

        <template v-if="isDistributionPointDelivery">
          <hr class="w-75 m-auto mt-3 mb-4" />
          <div class="form_title_wrapper">
            <h4>{{ $t("PLACEHOLDERS.distribution_point_data") }}</h4>
          </div>
          <base-input
            col="4"
            type="text"
            :placeholder="$t('PLACEHOLDERS.distribution_point_name')"
            :value="data.distribution_point_name"
            disabled
          />
          <base-input
            col="4"
            type="text"
            :placeholder="$t('PLACEHOLDERS.distribution_point_address')"
            :value="data.distribution_point_address"
            disabled
          />
          <base-input
            col="4"
            type="text"
            :placeholder="$t('PLACEHOLDERS.distribution_point_mobile')"
            :value="data.distribution_point_mobile"
            disabled
          />
        </template>

        <template v-if="hasAssignedDriver">
          <hr class="w-75 m-auto mt-3 mb-4" />
          <div class="form_title_wrapper">
            <h4>{{ $t("PLACEHOLDERS.package_driver_data") }}</h4>
          </div>
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.driver_name')"
            :value="data.driver_name"
            disabled
          />
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.driver_mobile')"
            :value="data.driver_mobile"
            disabled
          />
        </template>

        <div
          class="col-12 mt-4"
          v-if="data.status_history && data.status_history.length > 0"
        >
          <div class="form_title_wrapper mb-2">
            <h4>{{ $t("PLACEHOLDERS.status_history") }}</h4>
          </div>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center">
                    {{ $t("PLACEHOLDERS.status_name") }}
                  </th>
                  <th class="text-center">
                    {{ $t("PLACEHOLDERS.status_changed_at") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(history, index) in data.status_history"
                  :key="index"
                >
                  <td class="text-center">
                    {{ history.status || history.text || "-" }}
                  </td>
                  <td class="text-center">{{ history.created_at || "-" }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </div>
    </form>
    <!-- END:: Single Step Form Content -->

    <redelivery-confirmation-modal
      v-if="dialogRedeliveryConfirm"
      :modalAppearanceData="dialogRedeliveryConfirm"
      :selectedItem="selectedPackageForRedelivery"
      :isSubmitting="isRedeliverySubmitting"
      :errorMessage="redeliveryErrorMessage"
      @controleModalApperance="
        dialogRedeliveryConfirm = false;
        redeliveryErrorMessage = '';
      "
      @confirmRedelivery="handleRedeliveryConfirm"
    />

    <v-dialog
      v-model="dialogScan"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="520"
      content-class="return-scan-dialog"
      persistent
      scrollable
    >
      <v-card>
        <v-card-title class="text-h6 justify-center">{{
          $t("RETURN_REQUESTS.scanQr")
        }}</v-card-title>
        <v-card-text>
          <p class="mb-3">
            {{ $t("RETURN_REQUESTS.packageNumberLabel") }}:
            <strong>{{ data.package_number || "-" }}</strong>
          </p>

          <div class="mb-3">
            <div class="scanner_header mb-2">
              <p class="mb-0">
                {{ $t("RETURN_REQUESTS.scanningWithCamera") }}
              </p>
              <span
                class="scanner_state_badge"
                :class="{ active: isScannerActive }"
              >
                {{
                  isScannerActive
                    ? $t("RETURN_REQUESTS.cameraOn")
                    : $t("RETURN_REQUESTS.cameraOff")
                }}
              </span>
            </div>

            <p class="scanner_hint mb-2">
              {{ $t("RETURN_REQUESTS.scanHint") }}
            </p>

            <div v-if="availableCameras.length > 1" class="mb-2">
              <label class="camera_select_label mb-1 d-block">
                {{ $t("RETURN_REQUESTS.chooseCamera") }}
              </label>
              <select
                v-model="selectedCameraId"
                class="camera_select"
                @change="onCameraChange"
              >
                <option
                  v-for="camera in availableCameras"
                  :key="camera.id"
                  :value="camera.id"
                >
                  {{ camera.label || `${$t("RETURN_REQUESTS.camera")} ${camera.id}` }}
                </option>
              </select>
            </div>

            <div class="scanner_actions mb-2">
              <v-btn
                small
                outlined
                color="primary"
                :disabled="isScannerStarting"
                @click="initializeQrScanner"
              >
                {{ $t("RETURN_REQUESTS.restartCamera") }}
              </v-btn>
              <v-btn
                small
                outlined
                color="error"
                :disabled="!isScannerActive || isScannerStarting"
                @click="stopQrScanner"
              >
                {{ $t("RETURN_REQUESTS.stopCamera") }}
              </v-btn>
            </div>

            <div
              class="scanner_surface"
              :class="{ loading: isScannerStarting }"
            >
              <div id="package-show-qr-reader"></div>
            </div>

            <small v-if="cameraScanError" class="text-danger d-block mt-2">
              {{ cameraScanError }}
            </small>
          </div>

          <div class="row">
            <base-input
              col="12"
              type="text"
              :placeholder="$t('RETURN_REQUESTS.scanOrEnterQr')"
              v-model.trim="scannedToken"
            />
          </div>
        </v-card-text>
        <v-card-actions class="scan_actions">
          <v-btn
            class="modal_confirm_btn"
            :loading="isSubmittingScan"
            :disabled="isSubmittingScan"
            @click="confirmReturnByQr"
          >
            {{ $t("RETURN_REQUESTS.confirmReturn") }}
          </v-btn>
          <v-btn class="modal_cancel_btn" @click="closeScanDialog">
            {{ $t("BUTTONS.cancel") }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Html5Qrcode } from "html5-qrcode";
import RedeliveryConfirmationModal from "@/components/modals/RedeliveryConfirmationModal.vue";

export default {
  name: "ShowPackage",

  components: {
    RedeliveryConfirmationModal,
  },

  data() {
    return {
      isWaitingRequest: false,
      isSubmittingScan: false,
      isRedeliverySubmitting: false,
      dialogScan: false,
      dialogRedeliveryConfirm: false,
      redeliveryErrorMessage: "",
      scannedToken: null,
      qrScanner: null,
      cameraScanError: "",
      availableCameras: [],
      selectedCameraId: null,
      isScannerStarting: false,
      isScannerActive: false,
      data: {
        package_number: null,
        tracking_token: null,
        qr: null,
        user_name: null,
        mobile: null,
        address: null,
        bundle_number: null,
        weight: null,
        width: null,
        height: null,
        length: null,
        notes: null,
        status_text: null,
        status_value: null,
        delivery_options_value: null,
        delivery_options_text: null,
        status_history: [],
        distribution_point_name: null,
        distribution_point_address: null,
        distribution_point_mobile: null,
        driver_name: null,
        driver_mobile: null,
      },
    };
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
    isDistributionPointDelivery() {
      const value = (this.data.delivery_options_value || "").toLowerCase();
      const text = (this.data.delivery_options_text || "").toLowerCase();
      return (
        value === "distrbution_point" ||
        value === "distribution_point" ||
        text === "distrbution_point" ||
        text === "distribution_point"
      );
    },
    hasAssignedDriver() {
      return !!(this.data.driver_name || this.data.driver_mobile);
    },
    isReturningStatus() {
      return this.data.status_value === "returning";
    },
    isReturnedStatus() {
      return this.data.status_value === "returned";
    },
    hasReturnActions() {
      return this.isReturningStatus || this.isReturnedStatus;
    },
    selectedPackageForRedelivery() {
      return {
        package_number: this.data.package_number,
        tracking_token: this.data.tracking_token,
      };
    },
  },

  methods: {
    async downloadQR() {
      if (!this.data.qr) return;

      try {
        const response = await fetch(this.data.qr);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `package_${this.data.package_number}_qr.png`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$message.success(this.$t("MESSAGES.downloadedSuccessfully"));
      } catch (error) {
        console.error(error);
        this.$message.error(this.$t("MESSAGES.downloadFailed"));
      }
    },

    printQR() {
      if (this.data.qr) {
        const printWindow = window.open("", "_blank");
        const packageNumber = this.data.package_number;
        const trackingToken = this.data.tracking_token;
        const qrUrl = this.data.qr;
        const packageNumberLabel = this.$t("PLACEHOLDERS.package_number");
        const trackingTokenLabel = this.$t("PLACEHOLDERS.tracking_token");

        printWindow.document.write(
          "<html>" +
            "<head>" +
            "<title>" +
            this.$t("BUTTONS.print_qr") +
            " - " +
            packageNumber +
            "</title>" +
            "<style>" +
            "body {" +
            "display: flex;" +
            "flex-direction: column;" +
            "justify-content: center;" +
            "align-items: center;" +
            "height: 100vh;" +
            "margin: 0;" +
            "font-family: Arial, sans-serif;" +
            "}" +
            "img {" +
            "width: 300px;" +
            "height: 300px;" +
            "margin-bottom: 20px;" +
            "}" +
            ".info {" +
            "text-align: center;" +
            "}" +
            "h2 {" +
            "margin: 10px 0;" +
            "}" +
            "</style>" +
            "</head>" +
            "<body>" +
            '<div class="info">' +
            "<h2>" +
            packageNumberLabel +
            ": " +
            packageNumber +
            "</h2>" +
            "<h3>" +
            trackingTokenLabel +
            ": " +
            trackingToken +
            "</h3>" +
            "</div>" +
            '<img src="' +
            qrUrl +
            '" alt="QR Code" />' +
            "<script>" +
            "window.onload = function() {" +
            "window.print();" +
            "window.onafterprint = function() {" +
            "window.close();" +
            "}" +
            "}" +
            "<\/script>" +
            "</body>" +
            "</html>"
        );
        printWindow.document.close();
      }
    },

    async showPackage() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: `packages/${this.$route.params?.id}`,
        });

        const packageData = res.data.data || {};
        const distributionPoint =
          packageData.distrbution_point ||
          packageData.distribution_point ||
          packageData.delivery_point ||
          null;

        this.data.package_number = packageData.package_number;
        this.data.tracking_token = packageData.tracking_token;
        this.data.bundle_number = packageData.bundle_number;
        this.data.qr = packageData.qr;
        this.data.user_name = packageData.user?.name;
        this.data.mobile = packageData.mobile;
        this.data.weight = packageData.weight;
        this.data.address = packageData.address;
        this.data.width = packageData.width;
        this.data.height = packageData.height;
        this.data.length = packageData.length;
        this.data.notes = packageData.notes;
        this.data.status_text = packageData.status?.text || packageData.status;
        this.data.status_value = packageData.status?.value || null;
        this.data.delivery_options_value = packageData.delivery_options?.value;
        this.data.delivery_options_text = packageData.delivery_options?.text;
        this.data.status_history = packageData.status_history || [];
        this.data.distribution_point_name =
          distributionPoint?.name || distributionPoint?.title || null;
        this.data.distribution_point_address =
          distributionPoint?.address || null;
        this.data.distribution_point_mobile =
          distributionPoint?.mobile || distributionPoint?.phone || null;
        this.data.driver_name = packageData.driver?.name || null;
        this.data.driver_mobile =
          packageData.driver?.mobile || packageData.driver?.phone || null;
      } catch (error) {
        console.log(error?.response?.data?.message);
      }
    },

    openScanDialog() {
      this.scannedToken = null;
      this.cameraScanError = "";
      this.availableCameras = [];
      this.selectedCameraId = null;
      this.dialogScan = true;
      this.$nextTick(() => {
        this.initializeQrScanner();
      });
    },

    async closeScanDialog() {
      await this.stopQrScanner();
      this.dialogScan = false;
      this.scannedToken = null;
      this.cameraScanError = "";
      this.availableCameras = [];
      this.selectedCameraId = null;
    },

    async initializeQrScanner() {
      await this.stopQrScanner();
      this.cameraScanError = "";
      this.isScannerStarting = true;

      try {
        const cameras = await Html5Qrcode.getCameras();

        if (!cameras || cameras.length === 0) {
          this.cameraScanError = this.$t("RETURN_REQUESTS.cameraNotFound");
          this.isScannerActive = false;
          return;
        }

        this.availableCameras = cameras;

        const preferredCamera =
          cameras.find((camera) =>
            /back|rear|environment/i.test(camera.label || "")
          ) || cameras[0];

        this.selectedCameraId = preferredCamera.id;
        await this.startQrScanner(this.selectedCameraId);
      } catch (error) {
        this.cameraScanError = this.$t("RETURN_REQUESTS.cameraAccessError");
        this.isScannerActive = false;
      } finally {
        this.isScannerStarting = false;
      }
    },

    async startQrScanner(cameraId) {
      if (!this.dialogScan) {
        return;
      }

      const targetCameraId =
        cameraId || this.selectedCameraId || this.availableCameras[0]?.id;

      if (!targetCameraId) {
        this.cameraScanError = this.$t("RETURN_REQUESTS.cameraNotFound");
        return;
      }

      this.isScannerStarting = true;
      this.cameraScanError = "";
      await this.stopQrScanner();

      try {
        this.selectedCameraId = targetCameraId;
        this.qrScanner = new Html5Qrcode("package-show-qr-reader");

        await this.qrScanner.start(
          { deviceId: { exact: targetCameraId } },
          {
            fps: this.$vuetify.breakpoint.xsOnly ? 8 : 10,
            qrbox: this.$vuetify.breakpoint.xsOnly
              ? { width: 180, height: 180 }
              : { width: 220, height: 220 },
            aspectRatio: 1,
          },
          (decodedText) => {
            this.handleDecodedQr(decodedText);
          }
        );

        this.isScannerActive = true;
      } catch (error) {
        this.cameraScanError = this.$t("RETURN_REQUESTS.cameraAccessError");
        this.isScannerActive = false;
      } finally {
        this.isScannerStarting = false;
      }
    },

    async stopQrScanner() {
      const scanner = this.qrScanner;

      if (!scanner) {
        this.isScannerActive = false;
        return;
      }

      this.qrScanner = null;

      try {
        if (scanner.isScanning) {
          await scanner.stop();
        }
      } catch (error) {}

      try {
        await scanner.clear();
      } catch (error) {}

      this.isScannerActive = false;
    },

    onCameraChange() {
      this.startQrScanner(this.selectedCameraId);
    },

    async handleDecodedQr(decodedText) {
      const extractedToken = this.extractTokenFromQr(decodedText);

      if (!extractedToken) {
        return;
      }

      this.scannedToken = extractedToken;
      await this.stopQrScanner();
    },

    extractTokenFromQr(decodedText) {
      if (!decodedText || typeof decodedText !== "string") {
        return null;
      }

      const normalizedValue = decodedText.trim();
      if (!normalizedValue) {
        return null;
      }

      const tokenKeys = ["token", "tracking_token", "qr", "code"];
      const parsedJson = this.tryParseJson(normalizedValue);

      if (parsedJson) {
        for (let index = 0; index < tokenKeys.length; index++) {
          const key = tokenKeys[index];
          if (parsedJson[key]) {
            return String(parsedJson[key]).trim();
          }
        }
      }

      const parsedUrl = this.tryParseUrl(normalizedValue);
      if (parsedUrl) {
        for (let index = 0; index < tokenKeys.length; index++) {
          const key = tokenKeys[index];
          const queryToken = parsedUrl.searchParams.get(key);
          if (queryToken) {
            return queryToken.trim();
          }
        }

        const pathSegments = parsedUrl.pathname
          .split("/")
          .filter((segment) => segment);
        if (pathSegments.length > 0) {
          return pathSegments[pathSegments.length - 1];
        }
      }

      return normalizedValue;
    },

    tryParseJson(rawValue) {
      try {
        return JSON.parse(rawValue);
      } catch (error) {
        return null;
      }
    },

    tryParseUrl(rawValue) {
      try {
        return new URL(rawValue);
      } catch (error) {
        return null;
      }
    },

    async confirmReturnByQr() {
      if (!this.scannedToken) {
        this.$message.error(this.$t("RETURN_REQUESTS.enterQr"));
        return;
      }

      if (
        this.data.tracking_token &&
        this.scannedToken !== this.data.tracking_token
      ) {
        this.$message.error(this.$t("RETURN_REQUESTS.qrNotLinked"));
        return;
      }

      this.isSubmittingScan = true;
      try {
        await this.$axios({
          method: "POST",
          url: "packages/confirm-return",
          data: {
            token: this.scannedToken,
          },
        });

        this.$message.success(this.$t("RETURN_REQUESTS.returnConfirmed"));
        await this.closeScanDialog();
        await this.showPackage();
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("RETURN_REQUESTS.invalidQr")
        );
      } finally {
        this.isSubmittingScan = false;
      }
    },

    openRedeliveryConfirm() {
      this.redeliveryErrorMessage = "";
      this.dialogRedeliveryConfirm = true;
    },

    async handleRedeliveryConfirm() {
      this.isRedeliverySubmitting = true;
      this.redeliveryErrorMessage = "";

      try {
        await this.trySetPendingRequest(this.data.package_number);
        this.$message.success(this.$t("RETURN_REQUESTS.movedToPending"));
        this.dialogRedeliveryConfirm = false;
        await this.showPackage();
      } catch (error) {
        this.redeliveryErrorMessage =
          error.response?.data?.message ||
          this.$t("RETURN_REQUESTS.pendingFallbackError");
      } finally {
        this.isRedeliverySubmitting = false;
      }
    },

    async trySetPendingRequest(packageNumber) {
      await this.$axios({
        method: "POST",
        url: `packages/${packageNumber}/re-deliver`,
        data: { status: "pending" },
      });
    },
  },

  created() {
    this.showPackage();
  },

  beforeDestroy() {
    this.stopQrScanner();
  },
};
</script>

<style lang="scss" scoped>
.package_return_actions {
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  background: var(--theme_second_bg_clr, #f8f9fa);
  border: 1px solid var(--border_clr, #e9ecef);

  &__header {
    margin-bottom: 0.85rem;

    h5 {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: var(--theme_text_clr, #333);
    }
  }

  &__buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
}

.package_action_btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1.1rem;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  i {
    font-size: 0.95rem;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &--scan {
    background: #e8f4fd;
    color: #17a2b8;
    border-color: rgba(23, 162, 184, 0.2);

    &:hover {
      background: #17a2b8;
      color: #fff;
    }
  }

  &--pending {
    background: #fff4e5;
    color: #e67e22;
    border-color: rgba(243, 156, 18, 0.25);

    &:hover {
      background: #e67e22;
      color: #fff;
    }
  }
}

#package-show-qr-reader {
  width: 100%;
}

::v-deep .return-scan-dialog {
  width: min(520px, 95vw);
}

::v-deep .return-scan-dialog .v-card {
  max-height: 90vh;
  overflow-y: auto;
}

::v-deep .return-scan-dialog #package-show-qr-reader video {
  width: 100% !important;
  height: auto !important;
  border-radius: 8px;
}

.scanner_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.scanner_state_badge {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 999px;
  background: #f1f3f5;
  color: #6c757d;
}

.scanner_state_badge.active {
  background: #e8f7ef;
  color: #1e7e34;
}

.scanner_hint {
  font-size: 12px;
  color: #6c757d;
}

.camera_select_label {
  font-size: 12px;
  font-weight: 600;
}

.camera_select {
  width: 100%;
  min-height: 36px;
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  padding: 6px 10px;
  background: #fff;
}

.scanner_actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.scanner_surface {
  position: relative;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 10px;
  min-height: 220px;
  background: #fafafa;
}

.scanner_surface.loading {
  opacity: 0.75;
}

.scan_actions {
  gap: 8px;
  flex-wrap: wrap;
}
</style>
